import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";

export const REQUIRED_TUTOR_DOCUMENT_FIELDS = [
  { key: "fatherName", label: "Father's Name", kind: "text", required: true },
  { key: "aadhaarNumber", label: "Aadhaar Number", kind: "text", required: true },
  {
    key: "alternateContactNumber",
    label: "Alternative Contact Number",
    kind: "text",
    required: true,
  },
  { key: "email", label: "Email ID", kind: "text", required: true },
  { key: "completeAddress", label: "Complete Address", kind: "text", required: true, multiline: true },
  { key: "marksheet10th", label: "10th Marksheet", kind: "file", required: true },
  { key: "marksheet12th", label: "12th Marksheet", kind: "file", required: true },
  { key: "marksheetGraduation", label: "Graduation Marksheet", kind: "file", required: true },
  { key: "marksheetPG", label: "PG Marksheet (if any)", kind: "file", required: false },
  { key: "addressProof", label: "Address Proof", kind: "file", required: true },
  { key: "resume", label: "Updated Resume", kind: "file", required: true },
  {
    key: "experienceCertificate",
    label: "Experience Certificate (if any)",
    kind: "file",
    required: false,
  },
  {
    key: "competitiveExamScorecard",
    label: "Competitive Exam Scorecard (if cleared)",
    kind: "file",
    required: false,
  },
];

const FILE_KEYS = REQUIRED_TUTOR_DOCUMENT_FIELDS.filter((field) => field.kind === "file").map(
  (field) => field.key
);

export function createEmptyRequiredDocumentForm() {
  return {
    fatherName: "",
    aadhaarNumber: "",
    alternateContactNumber: "",
    email: "",
    completeAddress: "",
    files: FILE_KEYS.reduce((files, key) => {
      files[key] = { url: "", file: null, fileName: "" };
      return files;
    }, {}),
  };
}

export function requiredDocumentFormFromTutor(tutor) {
  const documents = tutor?.requiredDocuments || {};
  const form = createEmptyRequiredDocumentForm();
  form.fatherName = documents.fatherName || "";
  form.aadhaarNumber = documents.aadhaarNumber || "";
  form.alternateContactNumber = documents.alternateContactNumber || "";
  form.email = documents.email || "";
  form.completeAddress = documents.completeAddress || "";
  FILE_KEYS.forEach((key) => {
    form.files[key] = {
      url: documents[key]?.url || "",
      file: null,
      fileName: "",
    };
  });
  return form;
}

export function validateRequiredDocumentForm(form) {
  const fatherName = form.fatherName?.trim() || "";
  const aadhaarNumber = (form.aadhaarNumber || "").replace(/\s+/g, "");
  const alternateContactNumber = (form.alternateContactNumber || "").replace(/\s+/g, "");
  const email = form.email?.trim() || "";
  const completeAddress = form.completeAddress?.trim() || "";

  if (fatherName && fatherName.length < 2) {
    return "Father's name must be at least 2 characters";
  }
  if (aadhaarNumber && !/^\d{12}$/.test(aadhaarNumber)) {
    return "Aadhaar number must be 12 digits";
  }
  if (alternateContactNumber && !/^[6-9]\d{9}$/.test(alternateContactNumber)) {
    return "Alternative contact number must be a valid 10 digit mobile number";
  }
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return "Enter a valid email ID";
  }
  if (completeAddress && completeAddress.length < 10) {
    return "Complete address must be at least 10 characters";
  }

  const invalidFile = FILE_KEYS.find((key) => {
    const file = form.files?.[key]?.file;
    if (!file) return false;
    const extension = file.name.split(".").pop()?.toLowerCase();
    return !["pdf", "jpg", "jpeg", "png"].includes(extension);
  });
  if (invalidFile) {
    const label = REQUIRED_TUTOR_DOCUMENT_FIELDS.find((field) => field.key === invalidFile)?.label;
    return `${label} must be a PDF, JPG, or PNG file`;
  }

  return "";
}

function fileIsPresent(documents, key) {
  return Boolean(documents?.[key]?.url);
}

const REQUIRED_TEXT_KEYS = ["fatherName", "aadhaarNumber", "alternateContactNumber", "email", "completeAddress"];
const REQUIRED_FILE_KEYS = ["marksheet10th", "marksheet12th", "marksheetGraduation", "addressProof", "resume"];

export function getTutorDocumentStatus(tutor) {
  if (tutor?.tutorDocumentVerified) return "verified";
  const documents = tutor?.requiredDocuments || {};
  const textComplete = REQUIRED_TEXT_KEYS.every((key) => String(documents[key] || "").trim());
  const filesComplete = REQUIRED_FILE_KEYS.every((key) => fileIsPresent(documents, key));
  return textComplete && filesComplete ? "processing" : "pending";
}

export function tutorDocumentStatusLabel(status) {
  if (status === "verified") return "Document Verified";
  if (status === "processing") return "Document Processing";
  return "Document Pending";
}

export function RequiredTutorDocumentsView({ tutor }) {
  const documents = tutor?.requiredDocuments || {};
  const status = getTutorDocumentStatus(tutor);
  const verified = status === "verified";
  const processing = status === "processing";

  return (
    <ArgonBox
      mb={3}
      p={2}
      sx={{
        borderRadius: 2,
        border: verified ? "1px solid #86efac" : processing ? "1px solid #93c5fd" : "1px solid #fdba74",
        backgroundColor: verified ? "#f0fdf4" : processing ? "#eff6ff" : "#fff7ed",
      }}
    >
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <ArgonBox>
          <ArgonTypography variant="h6" fontWeight="medium" color="text">
            Required Documents
          </ArgonTypography>
          <ArgonTypography variant="caption" color="text">
            Submitted documents stay in Document Processing until an admin verifies them. PG marksheet,
            experience certificate, and competitive exam scorecard are optional.
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
            backgroundColor: verified ? "#16a34a" : processing ? "#1565c0" : "#ea580c",
            color: "#fff",
            whiteSpace: "nowrap",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {tutorDocumentStatusLabel(status)}
        </ArgonBox>
      </ArgonBox>
      <Grid container spacing={1.5}>
        {REQUIRED_TUTOR_DOCUMENT_FIELDS.map((field) => {
          const uploaded = field.kind === "file" && fileIsPresent(documents, field.key);
          const textValue = field.kind === "text" ? documents[field.key] : "";
          return (
            <Grid item xs={12} sm={6} md={4} key={field.key}>
              <ArgonBox
                p={1.5}
                sx={{ backgroundColor: "#fff", borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
              >
                <ArgonTypography variant="caption" fontWeight="bold" color="text">
                  {field.label}
                  {field.required ? " *" : ""}
                </ArgonTypography>
                {field.kind === "text" ? (
                  <ArgonTypography variant="button" display="block" color="text" sx={{ mt: 0.5 }}>
                    {textValue || "Not provided"}
                  </ArgonTypography>
                ) : uploaded ? (
                  <ArgonButton
                    component="a"
                    href={documents[field.key].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="text"
                    color="info"
                    size="small"
                    sx={{ p: 0, mt: 0.5, minWidth: "auto" }}
                  >
                    View file
                  </ArgonButton>
                ) : (
                  <ArgonTypography variant="button" display="block" color="secondary" sx={{ mt: 0.5 }}>
                    Not uploaded
                  </ArgonTypography>
                )}
              </ArgonBox>
            </Grid>
          );
        })}
        <Grid item xs={12} sm={6} md={4}>
          <ArgonBox
            p={1.5}
            sx={{ backgroundColor: "#fff", borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
          >
            <ArgonTypography variant="caption" fontWeight="bold" color="text">
              Educator Policy
            </ArgonTypography>
            {tutor?.educatorPolicy?.signedPdfUrl ? (
              <ArgonButton
                component="a"
                href={tutor.educatorPolicy.signedPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                color="info"
                size="small"
                sx={{ p: 0, mt: 0.5, minWidth: "auto" }}
              >
                View file
              </ArgonButton>
            ) : (
              <ArgonTypography variant="button" display="block" color="secondary" sx={{ mt: 0.5 }}>
                Not signed
              </ArgonTypography>
            )}
          </ArgonBox>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ArgonBox
            p={1.5}
            sx={{ backgroundColor: "#fff", borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
          >
            <ArgonTypography variant="caption" fontWeight="bold" color="text">
              Work Agreement
            </ArgonTypography>
            {tutor?.educatorPolicy?.signedWorkAgreementUrl ? (
              <ArgonButton
                component="a"
                href={tutor.educatorPolicy.signedWorkAgreementUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                color="info"
                size="small"
                sx={{ p: 0, mt: 0.5, minWidth: "auto" }}
              >
                View file
              </ArgonButton>
            ) : (
              <ArgonTypography variant="button" display="block" color="secondary" sx={{ mt: 0.5 }}>
                Not signed
              </ArgonTypography>
            )}
          </ArgonBox>
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

RequiredTutorDocumentsView.propTypes = {
  tutor: PropTypes.shape({
    requiredDocuments: PropTypes.object,
    tutorDocumentVerified: PropTypes.bool,
    tutorDocumentStatus: PropTypes.string,
    educatorPolicy: PropTypes.shape({
      signedPdfUrl: PropTypes.string,
      signedWorkAgreementUrl: PropTypes.string,
    }),
  }),
};

export function RequiredTutorDocumentsForm({ form, onTextChange, onFileChange }) {
  return (
    <Grid item xs={12}>
      <ArgonBox sx={{ border: "1px solid #e0e7ff", borderRadius: 2, p: 2, mb: 1 }}>
        <ArgonTypography variant="h6" mb={0.5}>
          Required Documents
        </ArgonTypography>
        <ArgonTypography variant="caption" color="text" display="block" mb={2}>
          Text fields and PDF, JPG, or PNG files. Optional files do not block submission. Admin verification is separate.
        </ArgonTypography>
        <Grid container spacing={2}>
          {REQUIRED_TUTOR_DOCUMENT_FIELDS.map((field) => {
            if (field.kind === "text") {
              return (
                <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.key}>
                  <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                    {field.label}
                    {field.required ? " *" : ""}
                  </ArgonTypography>
                  <ArgonInput
                    value={form[field.key] || ""}
                    onChange={(event) => onTextChange(field.key, event.target.value)}
                    placeholder={field.label}
                    multiline={Boolean(field.multiline)}
                    rows={field.multiline ? 3 : undefined}
                    fullWidth
                  />
                </Grid>
              );
            }

            const slot = form.files?.[field.key] || {};
            return (
              <Grid item xs={12} sm={6} key={field.key}>
                <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                  {field.label}
                  {field.required ? " *" : ""} (PDF, JPG, PNG)
                </ArgonTypography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  id={`required-doc-${field.key}`}
                  style={{ display: "none" }}
                  onChange={(event) => onFileChange(field.key, event.target.files?.[0] || null)}
                />
                <label htmlFor={`required-doc-${field.key}`}>
                  <ArgonButton component="span" variant="outlined" color="info" fullWidth>
                    {slot.file || slot.url ? "Change File" : "Upload File"}
                  </ArgonButton>
                </label>
                {(slot.fileName || slot.url) && (
                  <ArgonTypography variant="caption" color="success" display="block" mt={1}>
                    {slot.fileName || "Existing file uploaded"}
                    {slot.url && !slot.fileName ? (
                      <>
                        {" "}
                        <a href={slot.url} target="_blank" rel="noopener noreferrer">
                          View
                        </a>
                      </>
                    ) : null}
                  </ArgonTypography>
                )}
              </Grid>
            );
          })}
        </Grid>
      </ArgonBox>
    </Grid>
  );
}

RequiredTutorDocumentsForm.propTypes = {
  form: PropTypes.shape({
    fatherName: PropTypes.string,
    aadhaarNumber: PropTypes.string,
    alternateContactNumber: PropTypes.string,
    email: PropTypes.string,
    completeAddress: PropTypes.string,
    files: PropTypes.object,
  }).isRequired,
  onTextChange: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
};
